<template>
  <div class="st-root">
    <div v-if="sources.length === 0" class="st-empty">
      <i class="fa fa-database"></i>
      <p>No sources configured</p>
      <button v-if="canEdit" class="st-add-btn" @click="$emit('open-add-source')">
        <i class="fa fa-plus"></i> Add source
      </button>
    </div>

    <div v-else class="st-list">
      <div
        v-for="(source, idx) in sources"
        :key="`${source.tag}_${idx}`"
        class="st-card"
      >
        <div class="st-card-header">
          <div class="st-card-icon"><i class="fa fa-table"></i></div>
          <span class="st-card-tag">{{ source.tag }}</span>
          <button
            v-if="canEdit"
            class="st-remove-btn"
            @click="$emit('remove-source', idx)"
            title="Remove source"
          >
            <i class="fa fa-trash"></i>
          </button>
        </div>
        <div class="st-card-body">
          <editor-select-async
            :value="getSourceValue(source)"
            @input="(value) => $emit('update-source', { source, value })"
            :onFetch="(query, page) => onFetch(source, query, page)"
            :label="source.label"
            placeholder="Choose value…"
          />
        </div>
      </div>

      <button v-if="canEdit" class="st-add-card" @click="$emit('open-add-source')">
        <i class="fa fa-plus"></i>
        <span>Add source</span>
      </button>
    </div>
  </div>
</template>

<script>
import EditorSelectAsync from "./EditorSelectAsync.vue";

export default {
  name: "SourceTab",
  components: { EditorSelectAsync },
  props: {
    sources: {
      type: Array,
      required: true,
    },
    tabPosition: {
      type: String,
      required: true,
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
    getSourceValue: {
      type: Function,
      required: true,
    },
    onFetch: {
      type: Function,
      required: true,
    },
  },
};
</script>

<style scoped lang="scss">
$bg-base:     #0d0f17;
$bg-surface:  #13161f;
$bg-elevated: #1a1d29;
$bg-hover:    #1f2333;
$border:      #2d3250;
$border-sub:  #232840;
$accent:      #5b7cf6;
$text-primary: #e8eaf2;
$text-secondary: #7c87a8;
$text-muted:  #4a5270;
$danger:      #f87171;
$danger-dim:  rgba(#f87171, 0.12);
$radius:      6px;

.st-root {
  padding: 4px 0;
}

.st-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 28px 16px;
  gap: 8px;
  color: $text-muted;
  text-align: center;

  i { font-size: 28px; opacity: 0.5; }
  p { font-size: 13px; margin: 0; color: $text-muted; }
}

.st-add-btn {
  margin-top: 4px;
  background: rgba($accent, 0.12);
  border: 1px solid rgba($accent, 0.25);
  color: $accent;
  font-size: 12px;
  font-weight: 500;
  padding: 6px 14px;
  border-radius: $radius;
  cursor: pointer;
  outline: none;
  transition: background 0.15s;

  &:hover { background: rgba($accent, 0.2); }
  i { margin-right: 4px; }
}

.st-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 4px 0;
}

.st-card {
  background: $bg-elevated;
  border: 1px solid $border-sub;
  border-radius: $radius;
  transition: border-color 0.15s;

  &:hover { border-color: $border; }
}

.st-card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: $bg-surface;
  border-radius: $radius $radius 0 0;
  border-bottom: 1px solid $border-sub;
}

.st-card-icon {
  width: 24px;
  height: 24px;
  border-radius: 4px;
  background: rgba($accent, 0.12);
  color: $accent;
  font-size: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.st-card-tag {
  flex: 1;
  font-size: 12px;
  font-weight: 600;
  color: $text-primary;
  font-family: 'SFMono-Regular', 'Consolas', monospace;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.st-remove-btn {
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: $text-muted;
  cursor: pointer;
  font-size: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
  transition: background 0.15s, color 0.15s;
  flex-shrink: 0;

  &:hover { background: $danger-dim; color: $danger; }
}

.st-card-body {
  padding: 10px 12px;
}

.st-add-card {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 9px;
  background: transparent;
  border: 1px dashed $border;
  border-radius: $radius;
  color: $text-muted;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  outline: none;
  transition: border-color 0.15s, color 0.15s, background 0.15s;

  &:hover {
    border-color: $accent;
    color: $accent;
    background: rgba($accent, 0.05);
  }
}
</style>
